/**
 * GapAlarm — the strip at the top of every editor view (spec admin-scope-and-gaps-spec.md §4): which buckets of the
 * current scope will run dry in under two weeks, worst first, how far along the nearest item is, and a link to the
 * queue that fixes it, filtered to that bucket. Buckets: the county board, each city, school districts, special
 * districts, media (founder: "down to city only"); Federal and State have one each. A bucket with no harvester yet is
 * "not harvested yet", never red. Numbers from api_admin.scope_gaps — the same view the drafter and System Health read.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight } from 'lucide-react';
import { useScope, type ScopeGap } from '../lib/scope';
import { useEnv } from '../lib/env';

const PAGE_NAME: Record<string, string> = { 'sourced-votes': 'Sourced Votes', 'pipeline-triage': 'Triage', schedule: 'Schedule', 'upcoming-votes': 'Upcoming Votes' };
const day = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
const n = (k: number, one: string, many = `${one}s`) => `${k} ${k === 1 ? one : many}`;

/** Which queue moves this bucket forward, from how far along its nearest item is. */
function fixesIt(g: ScopeGap): { page: string; words: string } {
  switch (g.nearest_stage) {
    case 'approved': return { page: 'schedule', words: n(g.approved_waiting, 'vote') + ' approved, not scheduled' };
    case 'drafted': return { page: 'sourced-votes', words: n(g.drafts_in_review, 'draft') + ' in review' };
    case 'in pool': return { page: 'pipeline-triage', words: n(g.in_pool, 'item') + ' in the pool, none drafted' };
    case 'scheduled': return { page: 'upcoming-votes', words: n(g.scheduled_ahead, 'vote') + ' scheduled' };
    default: return { page: 'pipeline-triage', words: 'nothing in the pipeline' };
  }
}
function narrowFor(g: ScopeGap): string | null {
  if (g.bucket === 'city') return g.city_body_id ? `city:${g.city_body_id}` : 'cities';
  if (['county', 'schools', 'specials', 'media'].includes(g.bucket)) return g.bucket;
  return null;
}

export function GapAlarm() {
  const env = useEnv();
  const { ready, gaps, scope } = useScope();
  const [all, setAll] = useState(false);
  if (!ready || !scope) return null;

  // The county board is not a gap like the others (founder 2026-10-27): it has no day part of its own — it is the
  // cover vote on local days (Wed / Sat / Sun), and when no county vote is ready the scheduler covers the day with a
  // state or federal vote, which are plentiful (votd_reschedule_v3). So it never counts as a gap; it is a plain note.
  const red = gaps.filter(g => g.gap && g.bucket !== 'county');
  const cover = gaps.find(g => g.bucket === 'county' && g.lit);
  const coverNote = cover ? (cover.scheduled_ahead === 0 || !cover.runs_dry_on
    ? 'County cover: no county vote scheduled; local days use a state or federal cover vote.'
    : `County cover: last county vote scheduled ${day(cover.runs_dry_on)}; after that, local days use a state or federal cover vote.`) : null;
  const unlit = gaps.filter(g => !g.lit);
  const params = new URLSearchParams(window.location.search);
  const link = (g: ScopeGap, page: string) => {
    const p = new URLSearchParams();
    const sc = params.get('scope'); if (sc) p.set('scope', sc);
    const nw = narrowFor(g); if (nw) p.set('in', nw);
    return `/${env}/${page}?${p.toString()}`;
  };

  if (red.length === 0) {
    return (
      <div className="px-4 md:px-6 pt-3">
        <p className="inline-flex items-center gap-1.5 text-xs text-green-700"><CheckCircle2 size={13} />
          No gaps: every bucket here has two weeks or more ahead.{unlit.length > 0 && <span className="text-gray-text"> · Not harvested yet: {unlit.map(u => u.label).join(', ')}</span>}
        </p>
        {coverNote && <p className="mt-1 text-xs text-gray-text">{coverNote}</p>}
      </div>
    );
  }
  const shown = all ? red : red.slice(0, 4);
  return (
    <div className="px-4 md:px-6 pt-3">
      <div role="status" className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2">
        <div className="flex flex-wrap items-start gap-x-2 gap-y-1">
          <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-700" />
          <p className="text-xs font-semibold text-amber-900">{n(red.length, 'bucket')} under two weeks</p>
        </div>
        <ul className="mt-1 space-y-0.5 pl-5">
          {shown.map(g => {
            const f = fixesIt(g);
            const when = g.scheduled_ahead === 0 || !g.runs_dry_on ? 'nothing scheduled in the next 14 days' : `runs dry ${day(g.runs_dry_on)}`;
            return (
              <li key={`${g.bucket}:${g.city_body_id ?? ''}`} className="text-xs text-amber-950">
                <span className="font-semibold">{g.label}</span> — {when} · {f.words}{' '}
                <Link to={link(g, f.page)} className="font-medium text-brand-orange hover:underline whitespace-nowrap">Open {PAGE_NAME[f.page] ?? f.page}</Link>
              </li>
            );
          })}
        </ul>
        <div className="mt-1 pl-5 flex flex-wrap items-center gap-x-3 gap-y-1">
          {red.length > 4 && (
            <button className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-900 hover:underline" onClick={() => setAll(!all)} aria-expanded={all}>
              {all ? <ChevronDown size={12} /> : <ChevronRight size={12} />}{all ? 'Show fewer' : `${red.length - 4} more`}
            </button>
          )}
          {unlit.length > 0 && <span className="text-[11px] text-gray-text">Not harvested yet: {unlit.map(u => u.label).join(', ')}</span>}
          {coverNote && <span className="text-[11px] text-gray-text">{coverNote}</span>}
        </div>
      </div>
    </div>
  );
}
